import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, type ClassSession } from '../../api';

/** A datetime-local value from an ISO string, in the reader's own timezone. */
const forInput = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const when = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

type Draft = {
  name: string; startsAt: string; durationMinutes: string;
  capacity: string; location: string; conferenceUrl: string; description: string;
};

const blank: Draft = {
  name: '', startsAt: '', durationMinutes: '60',
  capacity: '', location: '', conferenceUrl: '', description: '',
};

/**
 * Scheduling classes on a live-session unit. Shown inside the unit editor, so
 * whoever is building the course can add the dates without leaving it.
 */
export function ClassSchedule({ courseId, unitId }: { courseId: number; unitId: number }) {
  const [sessions, setSessions] = useState<ClassSession[]>([]);
  const [draft, setDraft] = useState<Draft>(blank);
  const [editing, setEditing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ sessions?: ClassSession[] }>(`/courses/${courseId}/units/${unitId}`);
      setSessions(res.sessions ?? []);
    } catch (e) { setError((e as ApiError).message); }
  }, [courseId, unitId]);
  useEffect(() => { void load(); }, [load]);

  const body = () => ({
    name: draft.name.trim(),
    description: draft.description.trim() || null,
    // datetime-local is local time; the server stores the instant.
    startsAt: draft.startsAt ? new Date(draft.startsAt).toISOString() : '',
    durationMinutes: Number(draft.durationMinutes) || 60,
    capacity: draft.capacity.trim() === '' ? null : Number(draft.capacity),
    location: draft.location.trim() || null,
    conferenceUrl: draft.conferenceUrl.trim() || null,
  });

  const save = async () => {
    setBusy(true); setError(null); setNote(null);
    try {
      if (editing) {
        await api.patch(`/instructor/sessions/${editing}`, body());
        setNote('Class updated.');
      } else {
        await api.post(`/instructor/units/${unitId}/sessions`, body());
        setNote('Class scheduled.');
      }
      setDraft(blank); setEditing(null); setAdding(false);
      await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  const remove = async (x: ClassSession) => {
    const first = confirm(`Cancel "${x.name}" on ${when(x.startsAt)}?`);
    if (!first) return;
    setBusy(true); setError(null);
    try {
      await api.del(`/instructor/sessions/${x.id}`);
      setNote('Class cancelled.'); await load();
    } catch (e) {
      const err = e as ApiError;
      // The server refuses once while people hold places, and says how many.
      if (err.status === 409 && err.body?.registered) {
        if (confirm(`${err.message}\n\nCancel it anyway?`)) {
          try { await api.del(`/instructor/sessions/${x.id}?force=true`); setNote('Class cancelled.'); await load(); }
          catch (e2) { setError((e2 as ApiError).message); }
        }
      } else setError(err.message);
    } finally { setBusy(false); }
  };

  const startEdit = (x: ClassSession) => {
    setEditing(x.id); setAdding(true); setError(null);
    setDraft({
      name: x.name, startsAt: forInput(x.startsAt),
      durationMinutes: String(x.durationMinutes),
      capacity: x.capacity == null ? '' : String(x.capacity),
      location: x.location ?? '', conferenceUrl: x.conferenceUrl ?? '',
      description: x.description ?? '',
    });
  };

  const ready = draft.name.trim() !== '' && draft.startsAt !== '';

  return (
    <section className="panel">
      <div className="page-head">
        <h2 className="h2">Classes ({sessions.length})</h2>
        <button className="btn btn--primary" onClick={() => { setAdding(!adding); setEditing(null); setDraft(blank); }}>
          {adding ? 'Cancel' : 'Schedule a class'}
        </button>
      </div>

      {note && <p className="notice">{note}</p>}
      {error && <p className="auth__error">{error}</p>}

      {adding && (
        <div className="form">
          <label><span>Name</span>
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="Morning group" /></label>
          <label><span>Starts</span>
            <input type="datetime-local" value={draft.startsAt}
              onChange={(e) => setDraft({ ...draft, startsAt: e.target.value })} /></label>
          <label><span>Length in minutes</span>
            <input type="number" min={5} max={1440} value={draft.durationMinutes}
              onChange={(e) => setDraft({ ...draft, durationMinutes: e.target.value })} /></label>
          <label><span>Places</span>
            <input type="number" min={1} value={draft.capacity} placeholder="leave empty for no limit"
              onChange={(e) => setDraft({ ...draft, capacity: e.target.value })} /></label>
          <label><span>Where</span>
            <input value={draft.location} placeholder="Doha training room"
              onChange={(e) => setDraft({ ...draft, location: e.target.value })} /></label>
          <label><span>Joining link</span>
            <input value={draft.conferenceUrl} placeholder="https://…"
              onChange={(e) => setDraft({ ...draft, conferenceUrl: e.target.value })} /></label>
          <label><span>Notes for trainees</span>
            <textarea rows={3} value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></label>
          <div className="row">
            <button className="btn btn--primary" disabled={busy || !ready} onClick={() => void save()}>
              {busy ? 'Saving…' : editing ? 'Save changes' : 'Schedule it'}
            </button>
            <button className="btn btn--ghost" onClick={() => { setAdding(false); setEditing(null); setDraft(blank); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {sessions.length === 0 && !adding && <p className="muted">No classes scheduled.</p>}

      {sessions.length > 0 && (
        <table className="grid">
          <thead>
            <tr><th>Class</th><th>When</th><th>Where</th><th>Booked</th><th /></tr>
          </thead>
          <tbody>
            {sessions.map((x) => (
              <tr key={x.id} className={x.started ? 'is-off' : ''}>
                <td>{x.name}{x.started && <span className="tag">past</span>}</td>
                <td>{when(x.startsAt)} <span className="muted small">{x.durationMinutes} min</span></td>
                <td className="muted">{x.location ?? (x.conferenceUrl ? 'online' : '—')}</td>
                <td className="num">{x.taken}{x.capacity ? ` / ${x.capacity}` : ''}</td>
                <td className="row">
                  <Link to={`/marking/sessions/${x.id}`}>register</Link>
                  <button className="link-plain" disabled={busy} onClick={() => startEdit(x)}>edit</button>
                  <button className="link-danger" disabled={busy} onClick={() => void remove(x)}>cancel</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
